import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios'
import { env } from '@/lib/env'

/**
 * Shared axios instance for all API calls.
 *
 *   - Prefixes every URL with the configured API base.
 *   - Attaches the JWT access token on every request.
 *   - On 401, transparently refreshes the token once and retries (single-flight).
 *   - Surfaces API errors as {@link ApiError}.
 */

export interface ApiErrorPayload {
  timestamp: string
  status: number
  code: string
  message: string
  path: string
  errors?: { field: string; message: string }[]
}

export class ApiError extends Error {
  status: number
  code: string
  details?: ApiErrorPayload['errors']

  constructor(payload: ApiErrorPayload) {
    super(payload.message)
    this.status = payload.status
    this.code = payload.code
    this.details = payload.errors
  }
}

/** Dispatched when refresh fails — the auth store listens and clears the session. */
export const AUTH_LOGOUT_EVENT = 'profitpipe:auth-logout'

const ACCESS_TOKEN_KEY = 'profitpipe.accessToken'
const REFRESH_TOKEN_KEY = 'profitpipe.refreshToken'

export const tokenStorage = {
  getAccess: () => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  set: (accessToken: string, refreshToken?: string) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
    if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  },
  clear: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },
}

export const api: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl || '/',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccess()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// --- single-flight token refresh on 401 ---

let refreshPromise: Promise<string> | null = null

async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokenStorage.getRefresh()
  if (!refreshToken) throw new Error('no refresh token')
  // bare axios (not `api`) so this request itself can't recurse through the interceptor
  const resp = await axios.post(
    `${env.apiBaseUrl}/api/auth/refresh`,
    { refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  )
  tokenStorage.set(resp.data.accessToken, resp.data.refreshToken)
  return resp.data.accessToken as string
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorPayload>) => {
    const original = error.config as RetriableConfig | undefined
    const isAuthCall = original?.url?.includes('/api/auth/')

    if (error.response?.status === 401 && original && !original._retried && !isAuthCall && tokenStorage.getRefresh()) {
      original._retried = true
      try {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null
          })
        }
        const token = await refreshPromise
        original.headers = AxiosHeaders.from(original.headers)
        original.headers.set('Authorization', `Bearer ${token}`)
        return api(original)
      } catch {
        tokenStorage.clear()
        window.dispatchEvent(new Event(AUTH_LOGOUT_EVENT))
      }
    }

    if (error.response?.data) {
      return Promise.reject(new ApiError(error.response.data))
    }
    return Promise.reject(
      new ApiError({
        timestamp: new Date().toISOString(),
        status: error.response?.status ?? 0,
        code: 'network.error',
        message: error.message,
        path: error.config?.url ?? '',
      }),
    )
  },
)
